import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import APIRecords from './pages/APIRecords';
import ExcelGenerator from './pages/ExcelGenerator';
import { useAuth } from './hooks/useAuth';
// @ts-ignore
import confetti from 'canvas-confetti';

const birthdaySongUrl = '/happy-birthday-song.mp3'; // Place a suitable mp3 in public folder

const BirthdayOverlay: React.FC<{ name: string; onClose: () => void }> = ({ name, onClose }) => {
  const [countdown, setCountdown] = useState(29);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [audioRef, setAudioRef] = useState<HTMLAudioElement | null>(null);
  
  useEffect(() => {
    // Countdown timer
    const countdownInterval = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(countdownInterval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(countdownInterval);
  }, []);
  
  useEffect(() => {
    // Multiple confetti blasts for celebration
    const fireConfetti = () => {
      confetti({
        particleCount: 150,
        spread: 100,
        origin: { y: 0.6, x: 0.2 },
        colors: ['#ff6b6b', '#4ecdc4', '#45b7d1', '#96ceb4', '#feca57']
      });
      confetti({
        particleCount: 150,
        spread: 100,
        origin: { y: 0.6, x: 0.8 },
        colors: ['#ff9ff3', '#54a0ff', '#5f27cd', '#00d2d3', '#ff9f43']
      });
    };
    fireConfetti();
    const confettiInterval = setInterval(fireConfetti, 3000);
    
    // Enhanced emoji rain with more variety
    let emojiInterval = setInterval(() => {
      const emojis = ['🎉', '🎂', '🎈', '🎁', '🥳', '🍰', '🎊', '🌟', '✨', '🎀', '🎵', '💝', '🎪', '🎭'];
      const emoji = emojis[Math.floor(Math.random() * emojis.length)];
      const el = document.createElement('div');
      el.textContent = emoji;
      el.style.position = 'fixed';
      el.style.left = Math.random() * 95 + 'vw';
      el.style.top = '-3em';
      el.style.fontSize = (2 + Math.random() * 2) + 'rem';
      el.style.zIndex = '9998';
      el.style.transition = 'top 4s linear, transform 4s ease-in-out';
      el.style.transform = `rotate(${Math.random() * 360}deg)`;
      document.body.appendChild(el);
      setTimeout(() => { 
        el.style.top = '110vh'; 
        el.style.transform += ` rotate(${360 + Math.random() * 360}deg)`;
      }, 100);
      setTimeout(() => { 
        if (document.body.contains(el)) document.body.removeChild(el); 
      }, 4500);
    }, 200);
    
    // Setup birthday music
    const audio = new Audio();
    audio.loop = true;
    audio.volume = 0.6;
    audio.preload = 'auto';
    audio.src = birthdaySongUrl;
    setAudioRef(audio);
    
    // Try to play immediately, set state based on success
    audio.play().then(() => {
      setAudioPlaying(true);
      console.log('Birthday song playing automatically');
    }).catch(err => {
      console.log('Birthday song autoplay blocked:', err);
      setAudioPlaying(false);
    });
    
    // Auto-dismiss after 29 seconds (to match song duration)
    let timeout = setTimeout(() => { 
      onClose(); 
      audio.pause(); 
      clearInterval(emojiInterval); 
      clearInterval(confettiInterval);
    }, 29000);
    
    return () => { 
      audio.pause(); 
      audio.src = '';
      clearInterval(timeout); 
      clearInterval(emojiInterval); 
      clearInterval(confettiInterval);
    };
  }, [onClose]);
  
  return (
    <div className="fixed inset-0 bg-gradient-to-br from-blue-400 via-green-400 to-blue-500 flex flex-col items-center justify-center z-[9999] animate-pulse">
      <div className="text-center bg-white/20 backdrop-blur-sm rounded-3xl p-12 shadow-2xl border border-white/30">
        {/* DigiCash Logo with attractive rotating circles */}
        <div className="mb-6 relative flex justify-center items-center">
          <div className="absolute w-24 h-24 border-4 border-gradient-to-r from-yellow-400 to-orange-400 rounded-full animate-spin opacity-80" style={{animationDuration: '3s', borderImage: 'linear-gradient(45deg, #fbbf24, #f97316) 1'}}></div>
          <div className="absolute w-20 h-20 border-2 border-white/60 rounded-full animate-spin" style={{animationDuration: '2s', animationDirection: 'reverse'}}></div>
          <div className="absolute w-28 h-28 border border-blue-300/40 rounded-full animate-pulse"></div>
          <img src="/favicon.ico" alt="DigiCash" className="w-16 h-16 relative z-10" />
        </div>
        
        {/* Main Birthday Message */}
        <div className="text-8xl mb-6 animate-bounce" style={{animationDuration: '1.5s'}}>🎉🎂🎊</div>
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-black text-white mb-4 animate-pulse drop-shadow-2xl leading-relaxed px-4" style={{textShadow: '2px 2px 4px rgba(0,0,0,0.5), 0 0 20px rgba(255,255,255,0.3)'}}>
          जन्मदिन मुबारक!
        </h1>
        <h2 className="text-3xl font-bold text-white drop-shadow-lg mb-2">
          Happy Birthday {name || 'Dear'}! 🥳
        </h2>
        <div className="text-xl text-white/90 mb-6 font-semibold">
          🎈 DigiCash परिवार की तरफ से हार्दिक शुभकामनाएं! 🎈
        </div>
        
        {/* Celebration Message */}
        <div className="bg-gradient-to-r from-yellow-400 to-orange-400 text-white font-bold py-4 px-8 rounded-full text-lg shadow-lg mb-4 animate-bounce">
          🌟 आपका दिन शुभ हो, खुशियों से भरा हो! 🌟
        </div>
        
        {/* Audio control and countdown */}
        <div className="flex flex-col items-center space-y-2">
          {!audioPlaying && audioRef && (
            <button 
              onClick={() => {
                audioRef.play().then(() => {
                  setAudioPlaying(true);
                }).catch(err => console.log('Manual play failed:', err));
              }}
              className="bg-gradient-to-r from-green-400 to-blue-500 text-white font-bold py-2 px-6 rounded-full shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 animate-bounce"
            >
              🎵 Play Birthday Song 🎵
            </button>
          )}
          <div className="text-sm text-white/70 animate-pulse">
            {audioPlaying ? '🎵 Playing birthday song...' : '🎵 Click to play music'} | {countdown} seconds left
          </div>
        </div>
      </div>
    </div>
  );
};

const App: React.FC = () => {
  const location = useLocation();
  const { isAuthenticated, loading, user } = useAuth();
  const [showBirthday, setShowBirthday] = useState(false);
  const [birthdayChecked, setBirthdayChecked] = useState(false);
  
  useEffect(() => {
    if (isAuthenticated && user && (user as any).date_of_birth && !birthdayChecked) {
      const dob = new Date((user as any).date_of_birth);
      const today = new Date();
      if (dob.getDate() === today.getDate() && dob.getMonth() === today.getMonth()) {
        setShowBirthday(true);
      }
      setBirthdayChecked(true);
    }
  }, [isAuthenticated, user, birthdayChecked]);



  
  // Show birthday overlay on dashboard refresh if it's birthday
  useEffect(() => {
    if (location.pathname === '/dashboard' && isAuthenticated && user && (user as any).date_of_birth) {
      const dob = new Date((user as any).date_of_birth);
      const today = new Date();
      if (dob.getDate() === today.getDate() && dob.getMonth() === today.getMonth()) {
        // Always show on dashboard refresh if it's birthday
        setShowBirthday(true);
      }
    }
  }, [location.pathname, isAuthenticated, user]);
  useEffect(() => {
    // Dynamically set page title based on the current path
    const getTitle = (pathname: string) => {
      switch (pathname) {
        case '/login':
          return 'Back Office Management';
        case '/dashboard':
          return 'Query Management';
        case '/api-records':
          return 'API Records';
        case '/excel-generator':
          return 'Excel Generator';
        default:
          return 'Query Management';
      }
    };
    document.title = getTitle(location.pathname);
  }, [location.pathname]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div>Loading...</div>
      </div>
    );
  }

  if (showBirthday && user) {
    return <BirthdayOverlay name={user.name || ''} onClose={() => setShowBirthday(false)} />;
  }

  const ProtectedRoute: React.FC<{ element: React.ReactElement }> = ({ element }) => {
    return isAuthenticated ? element : <Navigate to="/login" state={{ from: location }} replace />;
  };

  return (
    <Routes>
      <Route path="/login" element={!isAuthenticated ? <Login /> : <Navigate to="/dashboard" />} />
      <Route path="/dashboard" element={<ProtectedRoute element={<Dashboard />} />} />
      <Route path="/api-records" element={<ProtectedRoute element={<APIRecords />} />} />
      <Route path="/excel-generator" element={<ProtectedRoute element={<ExcelGenerator />} />} />
      <Route path="/" element={<Navigate to={isAuthenticated ? '/dashboard' : '/login'} />} />
    </Routes>
  );
};

export default App; 
